import { readQueueRaw, writeQueueRaw } from "./db";

// Offline write queue (Sprint-002 S7, ADR-002 §8). Every completion/undo
// mutation the user makes while offline (or while a request simply fails)
// is enqueued here, keyed by the same `idempotency_key` generated at tap
// time, and replayed in FIFO order on reconnect. Idempotency means a
// replay after a flaky connection can never double-write server state.

export type QueueMutationKind =
  "complete_cadence" | "complete_assignment" | "uncomplete_assignment" | "uncomplete_cadence";

export interface CompleteCadencePayload {
  cadenceId: string;
  completedBy: string;
  completedAt: string; // ISO — captured at tap time, replayed verbatim (ADR-002 §8's last-write-wins reads server updated_at, not client replay time)
}

export interface CompleteAssignmentPayload {
  assignmentId: string;
  completedAt: string;
}

export interface UncompleteAssignmentPayload {
  assignmentId: string;
}

export interface UncompleteCadencePayload {
  completionId: string;
}

export type QueuePayload =
  | CompleteCadencePayload
  | CompleteAssignmentPayload
  | UncompleteAssignmentPayload
  | UncompleteCadencePayload;

export interface QueueItem {
  /** Queue-local id — distinct from idempotencyKey, used only for FIFO dedupe within this queue. */
  id: string;
  kind: QueueMutationKind;
  idempotencyKey: string;
  payload: QueuePayload;
  enqueuedAt: string; // ISO
}

/** A change to the queue's contents. `replayed` is true only when a
 * replay just sent at least one item and left nothing behind — the
 * offline banner's "All caught up" moment. */
export interface QueueChange {
  length: number;
  replayed: boolean;
}

type QueueListener = (change: QueueChange) => void;

const queueListeners = new Set<QueueListener>();

/**
 * Subscribe to queue changes (enqueue, cancel, clear, replay). Purely
 * observational — added for `OfflineBanner`'s truthful "N taps saved on
 * this phone" copy; it changes nothing about what is queued or replayed.
 * Returns the unsubscribe function.
 */
export function subscribeQueue(listener: QueueListener): () => void {
  queueListeners.add(listener);
  return () => {
    queueListeners.delete(listener);
  };
}

function notifyQueue(length: number, replayed = false): void {
  for (const listener of queueListeners) listener({ length, replayed });
}

function newId(): string {
  return crypto.randomUUID();
}

/**
 * Enqueue a mutation. Deduped on (kind, idempotencyKey) — if the exact same
 * logical mutation is already queued (e.g. a double-tap, or a retry after a
 * failed enqueue attempt), this is a no-op rather than a second queued
 * write. FIFO order is preserved: new items always append to the end.
 */
export async function enqueueMutation(
  kind: QueueMutationKind,
  idempotencyKey: string,
  payload: QueuePayload,
): Promise<QueueItem> {
  const queue = await readQueueRaw<QueueItem>();
  const existing = queue.find((q) => q.kind === kind && q.idempotencyKey === idempotencyKey);
  if (existing) return existing;

  const item: QueueItem = {
    id: newId(),
    kind,
    idempotencyKey,
    payload,
    enqueuedAt: new Date().toISOString(),
  };
  queue.push(item);
  await writeQueueRaw(queue);
  notifyQueue(queue.length);
  return item;
}

export async function getQueue(): Promise<QueueItem[]> {
  return readQueueRaw<QueueItem>();
}

export async function queueLength(): Promise<number> {
  return (await readQueueRaw<QueueItem>()).length;
}

export async function clearQueue(): Promise<void> {
  await writeQueueRaw<QueueItem>([]);
  notifyQueue(0);
}

async function removeItem(id: string): Promise<void> {
  const queue = await readQueueRaw<QueueItem>();
  await writeQueueRaw(queue.filter((q) => q.id !== id));
}

/**
 * Cancel a mutation that is still only local (never confirmed sent to the
 * server). Used when a task is un-done before its queued completion ever
 * synced — the completion never happened server-side, so there is
 * nothing to reverse over the network; we just remove the queued item.
 * Returns true if a matching item was found and removed.
 */
export async function cancelQueuedMutation(
  kind: QueueMutationKind,
  idempotencyKey: string,
): Promise<boolean> {
  const queue = await readQueueRaw<QueueItem>();
  const match = queue.find((q) => q.kind === kind && q.idempotencyKey === idempotencyKey);
  if (!match) return false;
  const next = queue.filter((q) => q.id !== match.id);
  await writeQueueRaw(next);
  notifyQueue(next.length);
  return true;
}

// Handlers receive the full queue item (not just its payload) so they can
// read `idempotencyKey` — every mutating API call requires the client-
// generated idempotency key, generated once at tap/enqueue time and
// replayed verbatim (never regenerated) so a retry can never double-write.
export type QueueHandlers = {
  [K in QueueMutationKind]: (item: QueueItem) => Promise<void>;
};

export interface ReplayOutcome {
  succeeded: QueueItem[];
  /** Permanently failed (non-network, non-auth HTTP error) — dropped from the queue, surfaced to the caller. */
  failed: Array<{ item: QueueItem; error: unknown }>;
  /** Still queued because a network error stopped replay (offline again mid-replay). */
  remaining: QueueItem[];
  /** True if replay stopped early because the session needs re-authentication
   * (a 401 — see design-spec-today-card.md's A5 auth task: "your link needs
   * a refresh"). Every item at/after the failing one is left queued, exactly
   * like a network-error stop — the taps are never lost, they just wait for
   * a fresh session (naturally resumed on next app load once the helper
   * re-opens their link). */
  requiresReauth: boolean;
}

function isNetworkError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "isNetworkError" in err &&
    Boolean((err as { isNetworkError?: unknown }).isNetworkError)
  );
}

function isAuthError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    (err as { status?: unknown }).status === 401
  );
}

/**
 * Replay every queued mutation in FIFO order. On a network error (offline
 * again mid-replay) OR a 401 (the session/link needs re-authentication) we
 * stop immediately and leave the remaining items queued for the next
 * attempt — order is preserved, nothing is dropped, in either case. On any
 * other permanent HTTP error we drop that single item (it will never
 * succeed on retry) and continue with the rest, so one bad mutation cannot
 * wedge the whole queue.
 */
export async function replayQueue(handlers: QueueHandlers): Promise<ReplayOutcome> {
  const queue = await readQueueRaw<QueueItem>();
  const succeeded: QueueItem[] = [];
  const failed: Array<{ item: QueueItem; error: unknown }> = [];

  for (const item of queue) {
    try {
      const handler = handlers[item.kind];
      await handler(item);
      succeeded.push(item);
      await removeItem(item.id);
    } catch (err) {
      if (isNetworkError(err) || isAuthError(err)) {
        // Offline again, or the session needs re-auth — stop here,
        // preserve remaining order for next replay. Never dropped.
        const remaining = (await readQueueRaw<QueueItem>()).filter((q) =>
          queue.slice(queue.indexOf(item)).some((r) => r.id === q.id),
        );
        notifyQueue(remaining.length);
        return { succeeded, failed, remaining, requiresReauth: isAuthError(err) };
      }

      // Permanent failure: drop it, keep going.
      failed.push({ item, error: err });
      await removeItem(item.id);
    }
  }

  const remaining = await readQueueRaw<QueueItem>();
  if (queue.length > 0) {
    notifyQueue(remaining.length, succeeded.length > 0 && remaining.length === 0);
  }
  return {
    succeeded,
    failed,
    remaining,
    requiresReauth: false,
  };
}
